// ---------------------------------------------------------------------------
// Map Loader — parses MapData JSON and builds a Three.js scene graph.
// Replicates the geometry-building logic that was previously hardcoded in
// scene.ts, but driven entirely by data.
// ---------------------------------------------------------------------------

import * as THREE from 'three';
import { addBoxCollider, clearColliders } from '../gameplay/collision';
import {
  buildWedgeBrush,
  buildSmoothRampBrush,
  buildDisplacementBrush,
  performCSG,
  autoGenerateColliders,
} from './brushes';
import { loadProps } from './props';
import { initDecals, loadStaticDecals } from './decals';
import type {
  MapData,
  BrushDef,
  LightDef,
  ColliderDef,
  WindowDef,
} from './map-format';

// ---------------------------------------------------------------------------
// Texture / Material system — delegates to scene.ts's existing mat() factory.
// We accept a material factory function so the loader stays decoupled from
// the specific texture implementation.
// ---------------------------------------------------------------------------

export type MatKey = string;

export interface MaterialFactory {
  /** Create or retrieve a cached MeshStandardMaterial for the given key + repeat. */
  get(key: string, repeatX?: number, repeatY?: number): THREE.MeshStandardMaterial;
}

// ---------------------------------------------------------------------------
// Geometry cache (shared across a single load)
// ---------------------------------------------------------------------------

const geoCache = new Map<string, THREE.BufferGeometry>();

/** Window composite materials (recess/glass/frame/ledge) tracked for disposal on map change. */
const windowMaterials: THREE.Material[] = [];

function boxGeo(w: number, h: number, d: number): THREE.BoxGeometry {
  const k = `b_${w}_${h}_${d}`;
  let g = geoCache.get(k) as THREE.BoxGeometry | undefined;
  if (!g) { g = new THREE.BoxGeometry(w, h, d); geoCache.set(k, g); }
  return g;
}

function cylGeo(rT: number, rB: number, h: number, seg: number): THREE.CylinderGeometry {
  const k = `c_${rT}_${rB}_${h}_${seg}`;
  let g = geoCache.get(k) as THREE.CylinderGeometry | undefined;
  if (!g) { g = new THREE.CylinderGeometry(rT, rB, h, seg); geoCache.set(k, g); }
  return g;
}

// ---------------------------------------------------------------------------
// Brush builders
// ---------------------------------------------------------------------------

function buildBoxBrush(
  scene: THREE.Scene,
  brush: BrushDef,
  matFactory: MaterialFactory,
): void {
  const [w, h, d] = brush.size ?? [1, 1, 1];
  const [x, y, z] = brush.position;
  const [rU, rV] = brush.texRepeat ?? [1, 1];
  const material = matFactory.get(brush.texture, rU, rV);
  const mesh = new THREE.Mesh(boxGeo(w, h, d), material);
  mesh.position.set(x, y, z);
  if (brush.rotation) {
    mesh.rotation.set(brush.rotation[0], brush.rotation[1], brush.rotation[2]);
  }
  mesh.castShadow = brush.castShadow !== false;
  mesh.receiveShadow = brush.receiveShadow !== false;
  scene.add(mesh);
  if (brush.collide !== false) {
    if (brush.rotation && (brush.rotation[0] !== 0 || brush.rotation[1] !== 0 || brush.rotation[2] !== 0)) {
      // For rotated boxes, compute the world-space AABB from the rotated mesh
      // so the collider matches the actual visual geometry.
      mesh.updateMatrixWorld(true);
      const box3 = new THREE.Box3().setFromObject(mesh);
      const center = new THREE.Vector3();
      const size = new THREE.Vector3();
      box3.getCenter(center);
      box3.getSize(size);
      addBoxCollider(center.x, center.y, center.z, size.x, size.y, size.z);
    } else {
      addBoxCollider(x, y, z, w, h, d);
    }
  }
}

function buildCylinderBrush(
  scene: THREE.Scene,
  brush: BrushDef,
  matFactory: MaterialFactory,
): void {
  const [rTop, rBot, h, segs] = brush.cylinder ?? [0.5, 0.5, 1, 8];
  const [x, y, z] = brush.position;
  const material = matFactory.get(brush.texture);
  const mesh = new THREE.Mesh(cylGeo(rTop, rBot, h, segs), material);
  mesh.position.set(x, y, z);
  mesh.castShadow = brush.castShadow !== false;
  mesh.receiveShadow = brush.receiveShadow !== false;
  scene.add(mesh);
  if (brush.collide !== false) {
    const r = Math.max(rTop, rBot);
    addBoxCollider(x, y, z, r * 2, h, r * 2);
  }
}

function buildPlaneBrush(
  scene: THREE.Scene,
  brush: BrushDef,
  matFactory: MaterialFactory,
): void {
  const [w, _h, d] = brush.size ?? [1, 0, 1];
  const [rU, rV] = brush.texRepeat ?? [1, 1];
  const material = matFactory.get(brush.texture, rU, rV);
  const mesh = new THREE.Mesh(new THREE.PlaneGeometry(w, d), material);
  const [x, y, z] = brush.position;
  mesh.position.set(x, y, z);
  // Default plane orientation: horizontal (rotated -90 on X)
  if (brush.rotation) {
    mesh.rotation.set(brush.rotation[0], brush.rotation[1], brush.rotation[2]);
  } else {
    mesh.rotation.x = -Math.PI / 2;
  }
  mesh.castShadow = false;
  mesh.receiveShadow = brush.receiveShadow !== false;
  scene.add(mesh);
}

function buildStairsBrush(
  scene: THREE.Scene,
  brush: BrushDef,
  matFactory: MaterialFactory,
): void {
  const [stepCount, stepW, stepH, stepD, dirZ] = brush.stairs ?? [5, 3, 0.2, 1.5, 1];
  const [startX, _y, startZ] = brush.position;
  const [rU, rV] = brush.texRepeat ?? [1, 1];
  for (let i = 0; i < stepCount; i++) {
    const totalH = stepH * (i + 1);
    const material = matFactory.get(brush.texture, rU, rV);
    const mesh = new THREE.Mesh(boxGeo(stepW, totalH, stepD), material);
    mesh.position.set(startX, totalH / 2, startZ + dirZ * stepD * i);
    mesh.castShadow = true;
    mesh.receiveShadow = true;
    scene.add(mesh);
    if (brush.collide !== false) {
      addBoxCollider(startX, totalH / 2, startZ + dirZ * stepD * i, stepW, totalH, stepD);
    }
  }
}

function buildRampBrush(
  scene: THREE.Scene,
  brush: BrushDef,
  matFactory: MaterialFactory,
): void {
  const [width, rise, run] = brush.rampDims ?? [3, 2, 10];
  const [x, _y, z] = brush.position;
  const rotY = brush.rotation?.[1] ?? 0;
  const length = Math.sqrt(rise * rise + run * run);
  const angle = Math.atan2(rise, run);
  const material = matFactory.get(brush.texture);
  const mesh = new THREE.Mesh(boxGeo(width, 0.2, length), material);
  mesh.position.set(x, rise / 2, z);
  mesh.rotation.x = -angle;
  mesh.rotation.y = rotY;
  mesh.castShadow = true;
  mesh.receiveShadow = true;
  scene.add(mesh);
  // Ramps are visual only — collision approximated by stair steps in callers
}

function buildArchBrush(
  scene: THREE.Scene,
  brush: BrushDef,
  matFactory: MaterialFactory,
): void {
  const [opening, height, depth, faceZNum] = brush.arch ?? [3, 4, 0.4, true];
  const faceZ = Boolean(faceZNum);
  const [x, _y, z] = brush.position;
  const pillarW = 0.6;
  const archRadius = opening / 2;
  const archCenterY = height;

  const makeMesh = (w: number, h: number, d: number, px: number, py: number, pz: number, collide: boolean, rU = 1, rV = 1) => {
    const material = matFactory.get(brush.texture, rU, rV);
    const mesh = new THREE.Mesh(boxGeo(w, h, d), material);
    mesh.position.set(px, py, pz);
    mesh.castShadow = true;
    mesh.receiveShadow = true;
    scene.add(mesh);
    if (collide) addBoxCollider(px, py, pz, w, h, d);
  };

  if (faceZ) {
    makeMesh(pillarW, height, depth, x - opening / 2 - pillarW / 2, height / 2, z, true, 1, 2);
    makeMesh(pillarW, height, depth, x + opening / 2 + pillarW / 2, height / 2, z, true, 1, 2);
    const segs = 7;
    for (let i = 0; i <= segs; i++) {
      const a = Math.PI * (i / segs);
      const bx = x + Math.cos(a) * archRadius;
      const by = archCenterY + Math.sin(a) * archRadius * 0.6;
      const bw = (Math.PI * archRadius) / segs * 0.9;
      makeMesh(bw, 0.4, depth, bx, by, z, false);
    }
  } else {
    makeMesh(depth, height, pillarW, x, height / 2, z - opening / 2 - pillarW / 2, true, 1, 2);
    makeMesh(depth, height, pillarW, x, height / 2, z + opening / 2 + pillarW / 2, true, 1, 2);
    const segs = 7;
    for (let i = 0; i <= segs; i++) {
      const a = Math.PI * (i / segs);
      const bz = z + Math.cos(a) * archRadius;
      const by = archCenterY + Math.sin(a) * archRadius * 0.6;
      const bh = (Math.PI * archRadius) / segs * 0.9;
      makeMesh(depth, 0.4, bh, x, by, bz, false);
    }
  }
}

// ---------------------------------------------------------------------------
// Window builder (composite: recess + glass + frame + ledge)
// ---------------------------------------------------------------------------

function buildWindow(
  scene: THREE.Scene,
  win: WindowDef,
): void {
  const [x, y, z] = win.position;
  const [w, h] = win.size;
  const faceZ = win.faceZ;
  const depth = 0.15;

  // Dark interior with warm emissive glow
  const recessMat = new THREE.MeshStandardMaterial({
    color: 0x1a1510,
    roughness: 0.3,
    metalness: 0.1,
    emissive: 0xffcc66,
    emissiveIntensity: 0.15,
  });
  windowMaterials.push(recessMat);
  const recess = new THREE.Mesh(
    boxGeo(faceZ ? w : depth, h, faceZ ? depth : w),
    recessMat,
  );
  recess.position.set(x, y, z);
  recess.receiveShadow = true;
  scene.add(recess);

  // Glass pane
  const glassMat = new THREE.MeshStandardMaterial({
    color: 0x88aacc,
    roughness: 0.05,
    metalness: 0.9,
    transparent: true,
    opacity: 0.3,
    envMapIntensity: 2.0,
  });
  windowMaterials.push(glassMat);
  const glassOffset = faceZ ? 0.08 : 0;
  const glassOffsetZ = faceZ ? 0 : 0.08;
  const glass = new THREE.Mesh(
    boxGeo(faceZ ? w * 0.9 : 0.02, h * 0.9, faceZ ? 0.02 : w * 0.9),
    glassMat,
  );
  glass.position.set(x + glassOffset, y, z + glassOffsetZ);
  glass.layers.enable(1); // visible on effect layer but raycaster (layer 0 only) passes through
  glass.layers.disable(0); // remove from layer 0 so bullets pass through glass
  scene.add(glass);

  // Window frame — material intentionally shared across frame bars within a single
  // window. Each window call creates its own material instance, so no cross-window sharing.
  const frameColor = new THREE.MeshStandardMaterial({ color: 0x5a4a3a, roughness: 0.6 });
  windowMaterials.push(frameColor);
  const frameW = 0.06;
  if (faceZ) {
    const topBar = new THREE.Mesh(boxGeo(w + 0.1, frameW, 0.1), frameColor);
    topBar.position.set(x, y + h / 2, z);
    scene.add(topBar);
    const botBar = new THREE.Mesh(boxGeo(w + 0.1, frameW, 0.1), frameColor);
    botBar.position.set(x, y - h / 2, z);
    scene.add(botBar);
  }

  // Ledge
  const ledgeThick = 0.12;
  const ledgeMat = new THREE.MeshStandardMaterial({ color: 0xa09888, roughness: 0.8 });
  windowMaterials.push(ledgeMat);
  const ledge = new THREE.Mesh(
    boxGeo(faceZ ? w + 0.2 : 0.3, ledgeThick, faceZ ? 0.3 : w + 0.2),
    ledgeMat,
  );
  ledge.position.set(x, y - h / 2 - ledgeThick / 2, z);
  ledge.castShadow = true;
  ledge.receiveShadow = true;
  scene.add(ledge);
}

// ---------------------------------------------------------------------------
// Light builder
// ---------------------------------------------------------------------------

function buildLight(scene: THREE.Scene, def: LightDef): void {
  let light: THREE.Light;

  switch (def.type) {
    case 'ambient': {
      light = new THREE.AmbientLight(def.color, def.intensity);
      break;
    }
    case 'hemisphere': {
      light = new THREE.HemisphereLight(def.color, def.groundColor ?? 0x000000, def.intensity);
      break;
    }
    case 'directional': {
      const dl = new THREE.DirectionalLight(def.color, def.intensity);
      if (def.position) dl.position.set(...def.position);
      if (def.castShadow) {
        dl.castShadow = true;
        if (def.shadowMapSize) dl.shadow.mapSize.set(def.shadowMapSize, def.shadowMapSize);
        if (def.shadowCamera) {
          const [near, far, left, right, top, bottom] = def.shadowCamera;
          dl.shadow.camera.near = near;
          dl.shadow.camera.far = far;
          dl.shadow.camera.left = left;
          dl.shadow.camera.right = right;
          dl.shadow.camera.top = top;
          dl.shadow.camera.bottom = bottom;
        }
        if (def.shadowBias !== undefined) dl.shadow.bias = def.shadowBias;
      }
      if (def.target) {
        dl.target.position.set(...def.target);
        scene.add(dl.target);
      }
      light = dl;
      break;
    }
    case 'point': {
      const pl = new THREE.PointLight(def.color, def.intensity, def.distance, def.decay);
      if (def.position) pl.position.set(...def.position);
      if (def.castShadow) pl.castShadow = true;
      light = pl;
      break;
    }
    case 'spot': {
      const sl = new THREE.SpotLight(def.color, def.intensity, def.distance, def.angle, def.penumbra, def.decay);
      if (def.position) sl.position.set(...def.position);
      if (def.castShadow) sl.castShadow = true;
      if (def.target) {
        sl.target.position.set(...def.target);
        scene.add(sl.target);
      }
      light = sl;
      break;
    }
    default:
      return;
  }

  scene.add(light);
}

// ---------------------------------------------------------------------------
// Awning builder (decorative tilted box)
// ---------------------------------------------------------------------------

function buildAwning(
  scene: THREE.Scene,
  brush: BrushDef,
  matFactory: MaterialFactory,
): void {
  const [w, _h, d] = brush.size ?? [8, 0.08, 4];
  const [x, y, z] = brush.position;
  const tiltX = brush.rotation?.[0] ?? -0.15;
  const material = matFactory.get(brush.texture);
  const mesh = new THREE.Mesh(boxGeo(w, 0.08, d), material);
  mesh.position.set(x, y, z);
  mesh.rotation.x = tiltX;
  mesh.castShadow = true;
  mesh.receiveShadow = true;
  scene.add(mesh);
}

// ---------------------------------------------------------------------------
// Public API
// ---------------------------------------------------------------------------

export interface LoadMapResult {
  /** The arena half-size from map meta. */
  arenaHalf: number;
}

/**
 * Load a map from MapData JSON into the given scene.
 * Clears existing colliders before building.
 */
export function loadMap(
  scene: THREE.Scene,
  mapData: MapData,
  matFactory: MaterialFactory,
): LoadMapResult {
  clearColliders();

  // Track meshes by label for CSG operations
  const labeledMeshes = new Map<string, THREE.Mesh>();

  // ---- Lights ----
  for (const lightDef of mapData.lights) {
    buildLight(scene, lightDef);
  }

  // ---- Brushes ----
  for (const brush of mapData.brushes) {
    // Capture child count BEFORE building so multi-mesh builders (stairs, arch)
    // don't fool us into labeling the final sub-mesh.
    const beforeCount = scene.children.length;

    switch (brush.shape) {
      case 'box':
        buildBoxBrush(scene, brush, matFactory);
        break;
      case 'cylinder':
        buildCylinderBrush(scene, brush, matFactory);
        break;
      case 'plane':
        buildPlaneBrush(scene, brush, matFactory);
        break;
      case 'stairs':
        buildStairsBrush(scene, brush, matFactory);
        break;
      case 'ramp':
        buildRampBrush(scene, brush, matFactory);
        break;
      case 'arch':
        buildArchBrush(scene, brush, matFactory);
        break;
      case 'wedge':
        if (brush.wedge) {
          buildWedgeBrush(
            scene, brush.position, brush.wedge, brush.texture,
            brush.texRepeat ?? [1, 1], matFactory, brush.collide !== false,
          );
        }
        break;
      case 'smooth_ramp':
        if (brush.smoothRamp) {
          buildSmoothRampBrush(
            scene, brush.position, brush.smoothRamp, brush.rotation,
            brush.texture, brush.texRepeat ?? [1, 1], matFactory, brush.collide !== false,
          );
        }
        break;
      case 'displacement':
        if (brush.displacement) {
          buildDisplacementBrush(
            scene, brush.position, brush.displacement, brush.texture,
            brush.texRepeat ?? [1, 1], matFactory, brush.collide !== false,
          );
        }
        break;
    }

    // Track labeled brushes for CSG operations.
    // Only label brushes that added exactly ONE mesh — multi-mesh builders
    // (stairs, arch) don't have a single coherent target for CSG and labeling
    // a random sub-mesh leads to wrong geometry being carved.
    if (brush.label) {
      const added = scene.children.length - beforeCount;
      if (added === 1) {
        const candidate = scene.children[scene.children.length - 1];
        if ((candidate as THREE.Mesh).isMesh) {
          labeledMeshes.set(brush.label, candidate as THREE.Mesh);
        } else {
          console.warn(`[map-loader] Brush label "${brush.label}" resolved to a non-mesh — skipping CSG target registration.`);
        }
      } else {
        console.warn(`[map-loader] Brush label "${brush.label}" on shape "${brush.shape}" produced ${added} meshes; CSG target registration skipped to avoid capturing the wrong sub-mesh.`);
      }
    }
  }

  // ---- CSG operations pass ----
  // Process brushes with CSG definitions (subtract doorways, carve windows, etc.)
  for (const brush of mapData.brushes) {
    if (brush.shape === 'csg_subtract' || brush.shape === 'csg_add') {
      if (!brush.csg) continue;
      const target = labeledMeshes.get(brush.csg.targetLabel);
      if (!target) continue;

      // Create tool geometry
      let toolGeo: THREE.BufferGeometry;
      if (brush.csg.toolShape === 'cylinder') {
        const [rT, rB, h, segs] = brush.csg.toolSize;
        toolGeo = new THREE.CylinderGeometry(rT, rB, h, segs || 8);
      } else {
        const [w, h, d] = brush.csg.toolSize;
        toolGeo = new THREE.BoxGeometry(w, h, d);
      }

      const toolMat = new THREE.MeshStandardMaterial();
      const toolMesh = new THREE.Mesh(toolGeo, toolMat);
      toolMesh.position.set(...brush.position);
      if (brush.rotation) {
        toolMesh.rotation.set(brush.rotation[0], brush.rotation[1], brush.rotation[2]);
      }

      const result = performCSG(target, toolMesh, brush.csg.operation);
      // Replace target in scene
      scene.remove(target);
      scene.add(result);

      // Update labeled meshes reference
      if (brush.csg.targetLabel) {
        labeledMeshes.set(brush.csg.targetLabel, result);
      }

      // Auto-generate colliders for the CSG result
      autoGenerateColliders(result);

      toolGeo.dispose();
      toolMat.dispose();
    }
  }

  // ---- Props ----
  if (mapData.props && mapData.props.length > 0) {
    loadProps(scene, mapData.props, matFactory);
  }

  // ---- Decals ----
  initDecals(scene);
  if (mapData.decals && mapData.decals.length > 0) {
    loadStaticDecals(scene, mapData.decals);
  }

  // ---- Explicit colliders ----
  for (const col of mapData.colliders) {
    const [x, y, z] = col.position;
    const [w, h, d] = col.size;
    addBoxCollider(x, y, z, w, h, d);
  }

  return {
    arenaHalf: mapData.meta.arenaHalf,
  };
}

/**
 * Build windows from an array of WindowDef. Called separately since windows
 * are composite objects not directly represented as a single brush.
 */
export function loadWindows(scene: THREE.Scene, windows: WindowDef[]): void {
  for (const win of windows) {
    buildWindow(scene, win);
  }
}

/** Dispose cached geometry created during loading. */
export function disposeLoaderCache(): void {
  geoCache.forEach(g => g.dispose());
  geoCache.clear();
  for (const mat of windowMaterials) mat.dispose();
  windowMaterials.length = 0;
}
